
import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import ConvertDate from "../helpers/DateConverter";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";
import { AuthContext } from "../helpers/AuthContext";

function Tools() {
  let navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const [ToolsObject, setToolsObject] = useState([]);
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState(
    searchParams.get("group") || localStorage.getItem("toolsGroupFilter") || ""
  );
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    async function fetchConfig() {
      let url = await loadConfig();
      setApiUrl(url.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loadingConfig) {
      axios.get(`${apiUrl}/tools`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((response) => {
        setToolsObject(response.data);
      });
    }
  }, [loadingConfig, apiUrl]);



  // Drop a persisted group filter that no longer matches any tool group
  useEffect(() => {
    if (groupFilter === "" || ToolsObject.length === 0) return;
    const groups = new Set(ToolsObject.map(tool => tool.ToolsGroup?.groupname).filter(Boolean));
    if (!groups.has(groupFilter)) {
      setGroupFilter("");
      localStorage.removeItem("toolsGroupFilter");
    }
  }, [ToolsObject, groupFilter]);

  // Filter tools by search and group
  const filteredTools = ToolsObject.filter(tool => {
    const serial = tool.serialnumber?.toLowerCase() || "";
    const type = tool.ToolsGroup?.groupname?.toLowerCase() || "";
    const searchTerm = search.toLowerCase();
    const matchesSearch = serial.includes(searchTerm) || type.includes(searchTerm);
    const matchesGroup = groupFilter === "" || tool.ToolsGroup?.groupname === groupFilter;
    const matchesDiscard = showDiscarded || tool.isDiscarded !== true;
    return matchesSearch && matchesGroup && matchesDiscard;
  });

  // Sort tools by selected column
  const sortedTools = React.useMemo(() => {
    if (!sortConfig.key) return filteredTools;
    const sorted = [...filteredTools].sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case "serialnumber":
          aValue = a.serialnumber || "";
          bValue = b.serialnumber || "";
          break;
        case "groupname":
          aValue = a.ToolsGroup?.groupname || "";
          bValue = b.ToolsGroup?.groupname || "";
          break;
        case "dateofpurchase":
          aValue = a.dateofpurchase || "";
          bValue = b.dateofpurchase || "";
          break;
        case "storagePosition":
          aValue = a.storagePosition || "";
          bValue = b.storagePosition || "";
          break;
        case "isOperational":
          aValue = a.isOperational === false ? 0 : 1;
          bValue = b.isOperational === false ? 0 : 1;
          break;
        default:
          aValue = "";
          bValue = "";
      }
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTools, sortConfig]);

  const handleGroupFilterChange = (e) => {
    setGroupFilter(e.target.value);
    localStorage.setItem("toolsGroupFilter", e.target.value);
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  // Prepare conditional rendering variables
  let content;
  if (loadingConfig) {
    content = <LoadingSpinner text="Lade Geräte..." />;
  } else if (ToolsObject.length === 0) {
    content = (
      <div className="container">
        <h1 className="my-4">Werkzeugliste</h1>
        {authState.isAdmin && (
          <div className="mb-4">
            <Link to="/Tools/add" className="btn btn-primary">Gerät hinzufügen</Link>
          </div>
        )}
        <div className="alert alert-info">Keine Geräte vorhanden.</div>
      </div>
    );
  } else {
    content = (
      <div className="container">
        <h1 className="my-4">Werkzeugliste</h1>
        {authState.isAdmin && (
          <div className="mb-4">
            <Link to="/Tools/add" className="btn btn-primary">Gerät hinzufügen</Link>
          </div>
        )}
        <div className="mb-3 toolbar" style={{ marginTop: '20px' }}>
          <select
            className="form-control toolbar-select"
            value={groupFilter}
            onChange={handleGroupFilterChange}
          >
            <option value="">Alle Gerätegruppen</option>
            {[...new Set(ToolsObject.map(tool => tool.ToolsGroup?.groupname).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((group, idx) => (
              <option key={idx} value={group}>{group}</option>
            ))}
          </select>
          <input
            type="text"
            className="form-control toolbar-search"
            placeholder="Suche nach Seriennummer oder Typ..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {/* Mobile only: the table header (and with it click-to-sort) is hidden on phones */}
          <div className="toolbar-sort">
            <select
              className="form-control"
              aria-label="Sortieren nach"
              value={sortConfig.key || ""}
              onChange={e => setSortConfig(prev => ({ key: e.target.value || null, direction: prev.direction }))}
            >
              <option value="">Sortierung: Standard</option>
              <option value="serialnumber">Seriennummer</option>
              <option value="groupname">Typ</option>
              <option value="dateofpurchase">Kaufdatum</option>
              <option value="storagePosition">Lagerort</option>
              <option value="owner">Eigentümer</option>
              <option value="isOperational">Status</option>
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              aria-label={sortConfig.direction === "asc" ? "Aufsteigend sortiert" : "Absteigend sortiert"}
              onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === "asc" ? "desc" : "asc" }))}
            >
              {sortConfig.direction === "asc" ? "▲" : "▼"}
            </button>
          </div>
          <div className="toolbar-check">
            <input
              type="checkbox"
              id="showDiscarded"
              checked={showDiscarded}
              onChange={e => setShowDiscarded(e.target.checked)}
            />
            <label htmlFor="showDiscarded">Ausgesonderte anzeigen</label>
          </div>
          <Link to="/qr-desktop" className="toolbar-icon" title="QR-Code scannen">
            <span style={{ display: 'inline-block', width: '2rem', height: '2rem', cursor: 'pointer' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                <rect x="2" y="4" width="2" height="16" />
                <rect x="6" y="4" width="1" height="16" />
                <rect x="9" y="4" width="2" height="16" />
                <rect x="13" y="4" width="1" height="16" />
                <rect x="16" y="4" width="2" height="16" />
                <rect x="20" y="4" width="1" height="16" />
              </svg>
            </span>
          </Link>
          <Link to="/qr-mobile" className="toolbar-icon" title="QR-Code senden (Handy)">
            <span style={{ display: 'inline-block', width: '2rem', height: '2rem', cursor: 'pointer' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
                <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" fill="currentColor"/>
              </svg>
            </span>
          </Link>
        </div>
        <table className="table table-stack">
          <thead>
            <tr>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("serialnumber")}>Seriennummer {sortConfig.key === "serialnumber" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("groupname")}>Typ {sortConfig.key === "groupname" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("dateofpurchase")}>Kaufdatum {sortConfig.key === "dateofpurchase" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("storagePosition")}>Lagerort {sortConfig.key === "storagePosition" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("owner")}>Eigentümer {sortConfig.key === "owner" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort("isOperational")}>Status {sortConfig.key === "isOperational" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}</th>
            </tr>
          </thead>
          <tbody>
            {sortedTools.map((value, key) => {
              return (
                <tr
                  key={key}
                  onClick={() => {
                    navigate(`/Tool/${value.id}`);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: value.isDiscarded ? '#f5f5f5' : 'transparent',
                    opacity: value.isDiscarded ? 0.6 : 1
                  }}
                >
                  <td data-label="Seriennummer">{value.serialnumber}</td>
                  <td data-label="Typ">{value.ToolsGroup.groupname}</td>
                  <td data-label="Kaufdatum">{ConvertDate(value.dateofpurchase)}</td>
                  <td data-label="Lagerort">{value.storagePosition || ""}</td>
                  <td data-label="Eigentümer">{value.owner || ""}</td>
                  <td data-label="Status">
                    {value.isDiscarded ? (
                      <span style={{ color: 'gray', fontSize: '1.2em' }} title="Ausgesondert">⊗</span>
                    ) : value.isOperational === false ? (
                      <span style={{ color: 'red', fontSize: '1.2em' }} title="Defekt">✗</span>
                    ) : (
                      <span style={{ color: 'green', fontSize: '1.2em' }} title="Betriebsbereit">✓</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }




  return (
    <>{content}</>
  );
}

export default Tools;
